import React from "react";
import Parse from "parse";

import { useTranslation } from "@opendash/core";
import { Button, Modal, Space, Divider, Upload, Pagination } from "antd";

import { Invoice, InvoicePage } from "../types";

import * as PDFJS from "pdfjs-dist";

import type { RcFile } from "antd/lib/upload";
import type { PDFDocumentProxy } from "pdfjs-dist/types/display/api";
import { $parse } from "@opendash/plugin-parse";

// TODO
// PDFJS.GlobalWorkerOptions.workerSrc =
//   "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.9.359/pdf.worker.js";
PDFJS.GlobalWorkerOptions.workerSrc = "/pdfjs-worker.js";

interface Props {
  visible: boolean;
  invoice: Invoice;
  onClose(): void;
  onChange(objectId: string): void;
}

export const InvoicePagePDFImport = React.memo<Props>(
  function InvoicePagePDFImport({ visible, invoice, onClose, onChange }) {
    const t = useTranslation();

    const previewContainerRef = React.useRef<HTMLCanvasElement>(null);

    const [file, setFile] = React.useState<RcFile | null>(null);
    const [pdf, setPDF] = React.useState<PDFDocumentProxy | null>(null);
    const [page, setPage] = React.useState(0);
    const [pageCount, setPageCount] = React.useState(0);

    React.useEffect(() => {
      if (!visible) {
        setFile(null);
      }
    }, [visible]);

    React.useEffect(() => {
      if (file && previewContainerRef.current) {
        const fileReader = new FileReader();

        fileReader.onload = function () {
          //@ts-ignore
          PDFJS.getDocument(new Uint8Array(fileReader.result)).promise.then(
            (pdf) => {
              setPDF(pdf);
            }
          );
        };

        fileReader.readAsArrayBuffer(file);
      } else {
        setPDF(null);
      }
    }, [file]);

    React.useEffect(() => {
      if (pdf && pdf.numPages > 0) {
        setPage(1);
        setPageCount(pdf.numPages);
      } else {
        setPage(0);
        setPageCount(0);
      }
    }, [pdf]);

    React.useEffect(() => {
      if (pdf && page > 0 && previewContainerRef.current) {
        pdf.getPage(page).then((page) => {
          const scale = 3;
          const viewport = page.getViewport({ scale });

          const canvas = previewContainerRef.current as HTMLCanvasElement;
          const context = canvas.getContext("2d") as CanvasRenderingContext2D;

          canvas.height = viewport.height;
          canvas.width = viewport.width;

          page.render({
            canvasContext: context,
            viewport: viewport,
          });
        });
      } else if (previewContainerRef.current) {
        previewContainerRef.current.width = 0;
        previewContainerRef.current.height = 0;
      }
    }, [pdf, page]);

    const importPage = React.useCallback(async () => {
      if (!previewContainerRef.current) {
        return;
      }

      const dataUrl = previewContainerRef.current.toDataURL("image/jpeg");

      const image = new Parse.File(
        `pdf-import-page-${page}.jpg`,
        { base64: dataUrl },
        "image/png"
      );

      await image.save();

      const preset = new InvoicePage({
        tenant: Parse.User.current()?.get("tenant"),
        invoice,
        image,
        title: `#${page}`,
      });

      $parse.ui.createObject(preset.className, preset).then((objectId) => {
        onChange(objectId);
      });
    }, [page]);

    return (
      <Modal
        title={t("app:admin.invoice_page_pdf_import.title")}
        visible={visible}
        onCancel={onClose}
        width={1000}
        footer={false}
      >
        <Space direction="horizontal">
          <Upload
            fileList={[]}
            beforeUpload={(file) => {
              setFile(file);

              return false;
            }}
          >
            <Button
              children={
                file
                  ? t("app:admin.invoice_page_pdf_import.action_upload_new")
                  : t("app:admin.invoice_page_pdf_import.action_upload")
              }
            />
          </Upload>

          <Divider type="vertical" />

          <Button
            type="primary"
            disabled={!pdf}
            onClick={importPage}
            children={t("app:admin.invoice_page_pdf_import.action_import")}
          />

          <Divider type="vertical" />

          {pageCount > 0 && (
            <Pagination
              current={page}
              pageSize={1}
              total={pageCount}
              showSizeChanger={false}
              onChange={(page) => {
                setPage(page);
              }}
            />
          )}
        </Space>

        <Divider />

        {!file && <p>{t("app:admin.invoice_page_pdf_import.no_file_hint")}</p>}

        <div style={{ width: "100%", maxHeight: "50vh", overflowY: "auto" }}>
          <canvas
            ref={previewContainerRef}
            style={{ width: "100%" }}
            width={0}
            height={0}
          />
        </div>
      </Modal>
    );
  }
);
